import { ReactNode, useMemo, useState } from 'react';
import { createSafeContext } from '@mews/framework';
import { PaymentMethod, PaymentPlan } from '@mews-commander/data-access';

interface PaymentMethodDetailDefaults {
    paymentMethodId: string;
    paymentMethod: PaymentMethod;
    sidebarTitle: ReactNode;
}

interface PaymentsTablesContextProps {
    openPaymentPlansDetails: (paymentPlanId: PaymentPlan['id']) => void;
    openPaymentMethodDetails: (defaults: PaymentMethodDetailDefaults) => void;

    closePaymentPlansDetails: () => void;
    closePaymentMethodDetails: () => void;

    selectedPaymentPlanId?: PaymentPlan['id'];
    selectedPaymentMethodDefaults?: PaymentMethodDetailDefaults;
}

const { PaymentsTablesContextProvider, usePaymentsTablesContext } = createSafeContext<
    'PaymentsTablesContext',
    PaymentsTablesContextProps
>('PaymentsTablesContext');

export const PaymentsTablesProvider = ({ children }: { children: ReactNode }) => {
    const [paymentPlanId, setPaymentPlanId] = useState<PaymentPlan['id'] | undefined>(undefined);
    const [paymentMethodDefaults, setPaymentMethodDefaults] = useState<PaymentMethodDetailDefaults | undefined>(
        undefined
    );

    const value: PaymentsTablesContextProps = useMemo(
        () => ({
            openPaymentPlansDetails: (id) => {
                setPaymentMethodDefaults(undefined);
                setPaymentPlanId(id);
            },
            openPaymentMethodDetails: (defaults) => {
                setPaymentPlanId(undefined);
                setPaymentMethodDefaults(defaults);
            },
            closePaymentPlansDetails: () => {
                setPaymentPlanId(undefined);
            },
            closePaymentMethodDetails: () => {
                setPaymentMethodDefaults(undefined);
            },
            selectedPaymentPlanId: paymentPlanId,
            selectedPaymentMethodDefaults: paymentMethodDefaults,
        }),
        [paymentPlanId, setPaymentPlanId, paymentMethodDefaults, setPaymentMethodDefaults]
    );

    return <PaymentsTablesContextProvider value={value}>{children}</PaymentsTablesContextProvider>;
};

export { usePaymentsTablesContext };
